import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  BriefcaseBusiness,
  Camera,
  Code2,
  Cpu,
  FolderGit2,
  Github,
  Home,
  Images,
  Linkedin,
  Mail,
  MapPin,
  Moon,
  RotateCcw,
  ShieldCheck,
  Menu,
  Send,
  Sun,
  User,
  Users,
  X,
} from 'lucide-react';

const imagePath = (name) => `${process.env.PUBLIC_URL}/site-images/${name}`;

const navItems = [
  { id: 'home', label: 'Home', Icon: Home },
  { id: 'about', label: 'About', Icon: User },
  { id: 'projects', label: 'Projects', Icon: FolderGit2 },
  { id: 'experience', label: 'Experience', Icon: BriefcaseBusiness },
  { id: 'gallery', label: 'Gallery', Icon: Images },
  { id: 'playground', label: 'Playground', Icon: Cpu },
  { id: 'contact', label: 'Contact', Icon: Send },
];

const galleryImages = [
  {
    src: imagePath('portrait-sunglasses.jpg'),
    alt: 'Harish wearing white sunglasses in a shop',
    className: 'md:row-span-2',
  },
  {
    src: imagePath('campus-red.jpeg'),
    alt: 'Red campus building and blue sky',
    className: '',
  },
  {
    src: imagePath('arduino-lab.jpeg'),
    alt: 'Arduino board connected to a laptop',
    className: '',
  },
  {
    src: imagePath('stadium-night.jpeg'),
    alt: 'Night football game from the stands',
    className: 'md:col-span-2',
  },
  {
    src: imagePath('portrait-car.jpg'),
    alt: 'Harish sitting in a car',
    className: '',
  },
  {
    src: imagePath('mural-car.jpeg'),
    alt: 'Street mural and painted car',
    className: '',
  },
  {
    src: imagePath('kensington.jpeg'),
    alt: 'Toronto street view in Kensington Market',
    className: '',
  },
  {
    src: imagePath('campus-stone.jpeg'),
    alt: 'Stone campus building and fall trees',
    className: '',
  },
  {
    src: imagePath('water.jpeg'),
    alt: 'Open water under a clear sky',
    className: 'md:col-span-2',
  },
];

const skills = [
  'Python',
  'C++',
  'Java',
  'JavaScript',
  'React',
  'Node.js',
  'Tailwind CSS',
  'PyTorch',
  'YOLO',
  'Open3D',
  'Gradio',
  'GitHub',
];

const experiences = [
  {
    org: 'CyberShieldMaple',
    role: 'Software Engineering Intern',
    dates: 'May 2026 - Aug 2026',
    detail:
      'Contributing to a cloud cybersecurity platform for SMEs, including collection-agent scripting, vulnerability analysis, and SIEM detection-rule development.',
    icon: ShieldCheck,
  },
  {
    org: 'Cansbridge Scholars',
    role: 'Scholar',
    dates: 'Apr 2026 - Present',
    detail:
      'Selected as a scholar in a leadership-focused program centered on entrepreneurship, global thinking, and ambitious student-led growth.',
    icon: Users,
  },
  {
    org: "Queen's Technology & Media Association (QTMA)",
    role: 'Developer',
    dates: 'Mar 2026 - Present',
    detail:
      'Contributing as a developer on student-led technology and media projects with a focus on practical product execution.',
    icon: Code2,
  },
  {
    org: "Queen's Startup Consulting (QSC)",
    role: 'Technical Consultant',
    dates: 'Mar 2026 - Present',
    detail:
      'Supporting startup consulting work with a technical perspective across product, software, and implementation decisions.',
    icon: BriefcaseBusiness,
  },
  {
    org: 'Smith Business & Technology',
    role: 'Sponsorship Coordinator',
    dates: 'Mar 2026 - Present',
    detail:
      'Helping coordinate sponsorship efforts and external partner relationships for business and technology programming.',
    icon: Users,
  },
  {
    org: 'Owning My',
    role: 'Growth Strategy | Operations & Technical Lead',
    dates: 'Mar 2026 - Apr 2026',
    detail:
      'Worked with the Riipen April cohort on growth strategy, operations, and technical execution for an early-stage initiative.',
    icon: Cpu,
  },
  {
    org: 'Adjunct Faculty Supervision',
    role: 'Software Engineering Assistant',
    dates: 'Sep 2025 - Present',
    detail:
      'Improving a real-time monitoring dashboard with React UI structure, reusable components, performance work, and secure coding documentation.',
    icon: BriefcaseBusiness,
  },
  {
    org: 'QHacks',
    role: 'Operations Director',
    dates: 'Sep 2025 - Present',
    detail:
      'Coordinating judge and mentor recruitment for a 350+ participant hackathon, while leading merchandise logistics and day-of event operations.',
    icon: Users,
  },
  {
    org: "Queen's Knights Robotics Team",
    role: 'Software - Computer Vision',
    dates: 'Sep 2025 - Apr 2026',
    detail:
      'Collected and labeled 100+ object-detection images, then trained and evaluated YOLO-based models for robotics vision tasks.',
    icon: Cpu,
  },
  {
    org: "Queen's AutoDrive Team",
    role: 'Perception - Localization, Mapping & Scene Segmentation',
    dates: 'Sep 2025 - Apr 2026',
    detail:
      'Converted LiDAR .las files to 3D point clouds and used DBSCAN, voxel downsampling, and Open3D to segment and visualize perception data.',
    icon: Code2,
  },
];

const projects = [
  {
    title: 'Words Add-In Citation',
    stack: 'TypeScript, Office.js, REST APIs',
    detail:
      'A Microsoft Word web add-in that analyzes highlighted text, fetches citations from a REST API, and inserts citation-linked formatting back into Word.',
    href: 'https://github.com/Harish-Kan/Words-Add-in-Citation',
    featured: true,
  },
  {
    title: 'Bubble Sorting Algorithm Visualizer',
    stack: 'Python, Gradio',
    detail:
      'An interactive Python app that visualizes Bubble Sort step-by-step, with user-controlled array size and value ranges, a Gradio web interface, and deployment through Hugging Face.',
    href: 'https://github.com/Harish-Kan/Bubble-Sort-Algorithm-Visualizer',
  },
  {
    title: "Queen's AutoDrive Label Reviewer",
    stack: 'Python',
    detail:
      'A label review tool for Queen’s AutoDrive workflows, built to make dataset checking and object-detection review faster and easier to manage.',
    href: 'https://github.com/Harish-Kan/Queens-AutoDrive-Label-Reviewer',
  },
  {
    title: 'Drag in Glycerine Lab Analysis',
    stack: 'Python',
    detail:
      'A physics lab analysis project studying whether drag force on small ball bearings falling through glycerine behaves linearly or quadratically.',
    href: 'https://github.com/Harish-Kan/Drag-in-Glycerine-Lab-Analysis',
  },
  {
    title: 'Thermal Microclimate Mapping Study',
    stack: 'C++, Python, Arduino',
    detail:
      'An Arduino-based temperature logging system using BME280 sensors and MicroSD integration, with Python analysis for averages, uncertainty, and comparative graphs.',
    href: 'https://github.com/Harish-Kan/Thermal-Microclimate-Mapping-Study',
  },
];

function ShowcaseControls({ onPrevious, onNext }) {
  return (
    <div className="mt-6 flex shrink-0 items-center justify-start gap-2 md:mt-0">
      <button
        type="button"
        onClick={onPrevious}
        className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-black/10 bg-white text-[#151515] shadow-sm transition hover:bg-[#151515] hover:text-white dark:border-white/10 dark:bg-[#171a21] dark:text-white dark:hover:bg-[#d5ff77] dark:hover:text-[#151515]"
        aria-label="Scroll previous"
      >
        <ArrowLeft size={18} />
      </button>
      <button
        type="button"
        onClick={onNext}
        className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-black/10 bg-white text-[#151515] shadow-sm transition hover:bg-[#151515] hover:text-white dark:border-white/10 dark:bg-[#171a21] dark:text-white dark:hover:bg-[#d5ff77] dark:hover:text-[#151515]"
        aria-label="Scroll next"
      >
        <ArrowRight size={18} />
      </button>
    </div>
  );
}

const winningLines = [
  [0, 1, 2],
  [3, 4, 5],
  [6, 7, 8],
  [0, 3, 6],
  [1, 4, 7],
  [2, 5, 8],
  [0, 4, 8],
  [2, 4, 6],
];

function getWinner(board) {
  for (const [a, b, c] of winningLines) {
    if (board[a] && board[a] === board[b] && board[a] === board[c]) {
      return board[a];
    }
  }
  if (board.every(Boolean)) return 'draw';
  return null;
}

function getComputerMove(board) {
  const emptySquares = board
    .map((value, index) => (value ? null : index))
    .filter((value) => value !== null);

  const scoreMove = (testBoard, isComputerTurn) => {
    const result = getWinner(testBoard);
    if (result === 'O') return 1;
    if (result === 'X') return -1;
    if (result === 'draw') return 0;

    const scores = testBoard
      .map((value, index) => (value ? null : index))
      .filter((value) => value !== null)
      .map((index) => {
        const nextBoard = [...testBoard];
        nextBoard[index] = isComputerTurn ? 'O' : 'X';
        return scoreMove(nextBoard, !isComputerTurn);
      });

    return isComputerTurn ? Math.max(...scores) : Math.min(...scores);
  };

  let bestMove = emptySquares[0];
  let bestScore = -Infinity;

  emptySquares.forEach((index) => {
    const nextBoard = [...board];
    nextBoard[index] = 'O';
    const score = scoreMove(nextBoard, false);
    if (score > bestScore) {
      bestScore = score;
      bestMove = index;
    }
  });

  return bestMove;
}

function GameMarker({ value }) {
  if (!value) return null;

  if (value === 'X') {
    return (
      <img
        src={imagePath('portrait-formal.jpg')}
        alt="X marker"
        className="h-full w-full rounded-2xl object-cover object-center"
      />
    );
  }

  return (
    <img
      src={imagePath('portrait-angry.png')}
      alt="O marker"
      className="h-full w-full rounded-2xl object-cover object-center"
    />
  );
}

function TicTacToeGame() {
  const [board, setBoard] = useState(Array(9).fill(null));
  const [isComputerThinking, setIsComputerThinking] = useState(false);
  const [score, setScore] = useState({ player: 0, computer: 0, draws: 0 });
  const scoredResultRef = useRef('');
  const result = getWinner(board);
  const isPlayerTurn = !result && !isComputerThinking;

  useEffect(() => {
    if (!result) return;

    const resultKey = `${board.join('-')}-${result}`;
    if (scoredResultRef.current === resultKey) return;
    scoredResultRef.current = resultKey;

    setScore((currentScore) => ({
      ...currentScore,
      player: currentScore.player + (result === 'X' ? 1 : 0),
      computer: currentScore.computer + (result === 'O' ? 1 : 0),
      draws: currentScore.draws + (result === 'draw' ? 1 : 0),
    }));
  }, [board, result]);

  const resetBoard = () => {
    setBoard(Array(9).fill(null));
    setIsComputerThinking(false);
  };

  const playSquare = (index) => {
    if (board[index] || !isPlayerTurn) return;

    const nextBoard = [...board];
    nextBoard[index] = 'X';
    setBoard(nextBoard);

    if (getWinner(nextBoard)) return;

    setIsComputerThinking(true);
    window.setTimeout(() => {
      setBoard((currentBoard) => {
        if (getWinner(currentBoard)) return currentBoard;
        const computerMove = getComputerMove(currentBoard);
        if (computerMove === undefined) return currentBoard;
        const computerBoard = [...currentBoard];
        computerBoard[computerMove] = 'O';
        return computerBoard;
      });
      setIsComputerThinking(false);
    }, 350);
  };

  const status =
    result === 'X'
      ? 'You won.'
      : result === 'O'
        ? 'Evil Harish won.'
        : result === 'draw'
          ? 'Draw game.'
          : isComputerThinking
            ? 'Evil Harish is thinking...'
            : 'Your move.';

  return (
    <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
      <div>
        <p className="mb-3 text-sm uppercase text-[#8a3c22]">Playground</p>
        <h2 className="text-4xl font-bold md:text-6xl">Tic tac toe</h2>
        <p className="mt-5 max-w-md leading-7 text-[#5d5d53] dark:text-white/65">
          Can you beat Evil Harish?
        </p>
        <div className="mt-7 grid max-w-sm grid-cols-3 gap-3 text-center">
          <div className="rounded-2xl bg-white p-4 ring-1 ring-black/10 dark:bg-[#171a21] dark:ring-white/10">
            <p className="text-2xl font-bold">{score.player}</p>
            <p className="text-xs uppercase text-[#5d5d53] dark:text-white/55">You</p>
          </div>
          <div className="rounded-2xl bg-white p-4 ring-1 ring-black/10 dark:bg-[#171a21] dark:ring-white/10">
            <p className="text-2xl font-bold">{score.computer}</p>
            <p className="text-xs uppercase text-[#5d5d53] dark:text-white/55">Evil Harish</p>
          </div>
          <div className="rounded-2xl bg-white p-4 ring-1 ring-black/10 dark:bg-[#171a21] dark:ring-white/10">
            <p className="text-2xl font-bold">{score.draws}</p>
            <p className="text-xs uppercase text-[#5d5d53] dark:text-white/55">Draws</p>
          </div>
        </div>
      </div>

      <div className="rounded-[2rem] bg-white p-5 shadow-sm ring-1 ring-black/10 dark:bg-[#171a21] dark:ring-white/10 md:p-7">
        <div className="mb-5 flex items-center justify-between gap-4">
          <p className="font-semibold text-[#34342f] dark:text-white/80">{status}</p>
          <button
            type="button"
            onClick={resetBoard}
            className="inline-flex items-center gap-2 rounded-full bg-[#151515] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#2c2c27] dark:bg-[#d5ff77] dark:text-[#151515] dark:hover:bg-[#c3f35c]"
          >
            <RotateCcw size={16} />
            Reset
          </button>
        </div>

        <div className="mx-auto grid aspect-square w-full max-w-[520px] grid-cols-3 gap-3">
          {board.map((value, index) => (
            <button
              key={index}
              type="button"
              onClick={() => playSquare(index)}
              disabled={Boolean(value) || Boolean(result) || isComputerThinking}
              className="aspect-square overflow-hidden rounded-3xl bg-[#f7f3ea] p-1 ring-1 ring-black/10 transition hover:scale-[1.02] hover:bg-[#efe9dc] disabled:cursor-default disabled:hover:scale-100 dark:bg-[#0f1115] dark:ring-white/10 dark:hover:bg-black"
              aria-label={`Tic tac toe square ${index + 1}`}
            >
              <GameMarker value={value} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function SplashPage({ onEnter }) {
  return (
    <section className="min-h-screen bg-[#f7f3ea] px-4 py-10 text-[#151515]">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] w-full max-w-5xl items-center justify-center">
        <div className="w-full max-w-3xl rounded-[2rem] bg-white px-5 pb-10 pt-6 text-center shadow-sm ring-1 ring-black/10 sm:px-10 sm:pb-12">
          <div
            className="h-[240px] w-full bg-contain bg-center bg-no-repeat sm:h-[340px] md:h-[390px]"
            style={{
              backgroundImage:
                'url(https://cdn.dribbble.com/users/285475/screenshots/2083086/dribbble_1.gif)',
            }}
            aria-hidden="true"
          >
            <h1 className="pt-6 text-6xl font-bold sm:pt-8 sm:text-7xl md:text-8xl">404</h1>
          </div>

          <h2 className="mb-4 text-2xl font-bold sm:text-3xl">Looks like you're lost</h2>
          <p className="mx-auto mb-6 max-w-xl text-base text-[#4b4b43] sm:text-lg">
            But, good thing you found Harish Karikaran Kandavell's personal website!
          </p>

          <button
            type="button"
            onClick={onEnter}
            className="my-5 rounded-full bg-[#1f6f43] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-green-900/15 transition hover:bg-[#185934] focus:outline-none focus:ring-2 focus:ring-[#1f6f43] focus:ring-offset-2"
          >
            Go to Home
          </button>
        </div>
      </div>
    </section>
  );
}

export default function PersonalWebsite() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [showSplash, setShowSplash] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem('theme') === 'dark');
  const projectsRef = useRef(null);
  const experienceRef = useRef(null);

  const scrollShowcase = (ref, direction) => {
    if (!ref.current) return;
    ref.current.scrollBy({
      left: direction * Math.min(ref.current.clientWidth * 0.85, 560),
      behavior: 'smooth',
    });
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    setIsMenuOpen(false);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  if (showSplash) {
    return <SplashPage onEnter={() => setShowSplash(false)} />;
  }

  return (
    <div className={`${isDarkMode ? 'dark' : ''}`}>
    <div className="min-h-screen bg-[#f7f3ea] text-[#151515] transition-colors duration-300 dark:bg-[#0f1115] dark:text-[#f4f1ea]">
      <nav className="fixed inset-x-0 top-0 z-50 border-b border-black/10 bg-[#f7f3ea]/90 backdrop-blur-xl transition-colors duration-300 dark:border-white/10 dark:bg-[#0f1115]/90">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 md:px-6">
          <button
            type="button"
            onClick={() => scrollToSection('home')}
            className="text-left text-lg font-bold leading-none"
          >
            Harish Karikaran Kandavell
          </button>

          <div className="hidden items-center gap-1 rounded-full border border-black/10 bg-white/60 p-1.5 shadow-sm backdrop-blur dark:border-white/10 dark:bg-white/10 md:flex">
            {navItems.map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => scrollToSection(id)}
                className={`group inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm transition-all duration-200 hover:px-4 hover:scale-105 ${
                  activeSection === id ? 'bg-[#151515] text-white shadow-sm dark:bg-[#d5ff77] dark:text-[#151515]' : 'text-[#4b4b43] hover:bg-black/5 dark:text-white/70 dark:hover:bg-white/10'
                }`}
              >
                <Icon size={16} className="transition-transform duration-200 group-hover:scale-110" />
                <span>{label}</span>
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setIsDarkMode((current) => !current)}
            className="hidden items-center gap-2 rounded-full bg-[#151515] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#2c2c27] dark:bg-[#d5ff77] dark:text-[#151515] dark:hover:bg-[#c3f35c] md:inline-flex"
            aria-label="Toggle dark mode"
          >
            {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
            {isDarkMode ? 'Light' : 'Dark'}
          </button>

          <button
            type="button"
            className="rounded-full border border-black/10 p-2 dark:border-white/15 md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle navigation"
          >
            {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {isMenuOpen && (
          <div className="border-t border-black/10 bg-[#f7f3ea] px-4 py-3 dark:border-white/10 dark:bg-[#0f1115] md:hidden">
            <button
              type="button"
              onClick={() => setIsDarkMode((current) => !current)}
              className="mb-2 flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm text-[#34342f] hover:bg-black/5 dark:text-white/80 dark:hover:bg-white/10"
            >
              {isDarkMode ? <Sun size={17} /> : <Moon size={17} />}
              {isDarkMode ? 'Light mode' : 'Dark mode'}
            </button>
            {navItems.map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => scrollToSection(id)}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm text-[#34342f] hover:bg-black/5 dark:text-white/80 dark:hover:bg-white/10"
              >
                <Icon size={17} />
                {label}
              </button>
            ))}
          </div>
        )}
      </nav>

      <main>
        <section id="home" className="px-4 pb-20 pt-28 md:px-6 md:pb-28 md:pt-36">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-end">
            <div>
              <p className="mb-6 inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/60 px-4 py-2 text-sm text-[#4b4b43] dark:border-white/10 dark:bg-white/10 dark:text-white/70">
                <MapPin size={16} />
                Toronto / Kingston
              </p>
              <h1 className="max-w-4xl text-6xl font-bold leading-[0.95] tracking-normal sm:text-7xl lg:text-8xl">
                Computing student building useful things with taste.
              </h1>
              <p className="mt-7 max-w-2xl text-lg leading-8 text-[#4b4b43] dark:text-white/70 md:text-xl">
                I'm Harish, a Queen's Computing student interested in web development,
                computer vision, cybersecurity, robotics, and the kind of projects that feel good to use.
              </p>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => scrollToSection('projects')}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#151515] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#2c2c27] dark:bg-[#d5ff77] dark:text-[#151515] dark:hover:bg-[#c3f35c]"
                >
                  See Projects
                  <ArrowUpRight size={17} />
                </button>
                <button
                  type="button"
                  onClick={() => scrollToSection('gallery')}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-black/15 px-6 py-3 text-sm font-semibold transition hover:bg-white/70 dark:border-white/15 dark:hover:bg-white/10"
                >
                  Photo Log
                  <Camera size={17} />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-6 gap-3">
              <img
                src={imagePath('portrait-formal.jpg')}
                alt="Harish Karikaran Kandavell portrait"
                className="col-span-4 h-[480px] w-full rounded-[2rem] object-cover shadow-2xl shadow-black/15"
              />
              <div className="col-span-2 grid gap-3">
                <img
                  src={imagePath('campus-red.jpeg')}
                  alt="Queen's campus building"
                  className="h-[232px] w-full rounded-[1.5rem] object-cover"
                />
                <img
                  src={imagePath('mural-car.jpeg')}
                  alt="Toronto mural and painted car"
                  className="h-[232px] w-full rounded-[1.5rem] object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section id="about" className="border-y border-black/10 bg-[#151515] px-4 py-20 text-white dark:border-white/10 dark:bg-black md:px-6">
          <div className="mx-auto grid max-w-7xl gap-10 md:grid-cols-[0.8fr_1.2fr] md:items-start">
            <div className="md:sticky md:top-28">
              <p className="mb-4 text-sm uppercase text-[#d5ff77]">About</p>
              <h2 className="text-4xl font-bold leading-tight md:text-6xl">Really Loving Codex.</h2>
            </div>
            <div>
              <div className="mt-10 flex flex-wrap gap-3">
                {skills.map((skill) => (
                  <span
                    key={skill}
                    className="rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm text-white/80"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="projects" className="px-4 py-20 md:px-6 md:py-28">
          <div className="mx-auto max-w-7xl">
            <div className="mb-12 flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <p className="mb-3 text-sm uppercase text-[#8a3c22]">Projects</p>
                <h2 className="text-4xl font-bold md:text-6xl">Applied software builds</h2>
              </div>
              <p className="max-w-xl text-[#5d5d53] dark:text-white/65">
                A focused set of projects across developer tools, data analysis, robotics workflows,
                and interactive computing.
              </p>
              <ShowcaseControls
                onPrevious={() => scrollShowcase(projectsRef, -1)}
                onNext={() => scrollShowcase(projectsRef, 1)}
              />
            </div>

            <div
              ref={projectsRef}
              className="-mx-4 flex snap-x gap-4 overflow-x-auto px-4 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {projects.map((project) => (
                <article
                  key={project.title}
                  className="flex min-h-[330px] min-w-[min(430px,86vw)] snap-start flex-col justify-between rounded-[2rem] bg-white p-6 shadow-sm ring-1 ring-black/10 transition-colors duration-300 dark:bg-[#171a21] dark:ring-white/10"
                >
                  <div>
                    <div className="mb-4 flex flex-wrap items-center gap-3">
                      {project.featured && (
                        <p className="text-sm uppercase text-[#8a3c22] dark:text-[#d5ff77]">Featured Project</p>
                      )}
                      <span className="rounded-full bg-[#151515] px-3 py-1 text-xs font-semibold uppercase text-[#d5ff77]">
                        {project.stack}
                      </span>
                    </div>
                    <h3 className="text-2xl font-bold">{project.title}</h3>
                    <p className="mt-4 leading-7 text-[#5d5d53] dark:text-white/65">{project.detail}</p>
                  </div>
                  <a
                    href={project.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-8 inline-flex w-fit items-center gap-2 rounded-full bg-[#151515] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#2c2c27] dark:bg-[#d5ff77] dark:text-[#151515] dark:hover:bg-[#c3f35c]"
                  >
                    <Github size={17} />
                    View Code
                  </a>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="experience" className="border-y border-black/10 bg-white/45 px-4 py-20 dark:border-white/10 dark:bg-white/[0.03] md:px-6 md:py-28">
          <div className="mx-auto max-w-7xl">
            <div className="mb-12 flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <p className="mb-3 text-sm uppercase text-[#8a3c22]">Experience</p>
                <h2 className="text-4xl font-bold md:text-6xl">Teams and roles</h2>
              </div>
              <p className="max-w-xl text-[#5d5d53] dark:text-white/65">
                Cybersecurity, robotics, consulting, hackathon operations, and campus organizations.
              </p>
              <ShowcaseControls
                onPrevious={() => scrollShowcase(experienceRef, -1)}
                onNext={() => scrollShowcase(experienceRef, 1)}
              />
            </div>

            <div
              ref={experienceRef}
              className="-mx-4 flex snap-x gap-4 overflow-x-auto px-4 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {experiences.map(({ org, role, dates, detail, icon: Icon }) => (
                <article
                  key={org}
                  className="min-w-[min(390px,86vw)] snap-start rounded-[2rem] bg-white p-6 shadow-sm ring-1 ring-black/10 transition-colors duration-300 dark:bg-[#171a21] dark:ring-white/10"
                >
                  <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-[#d5ff77] text-[#151515]">
                    <Icon size={20} />
                  </div>
                  <p className="text-sm text-[#8a3c22] dark:text-[#d5ff77]">{dates}</p>
                  <h3 className="mt-2 text-2xl font-bold">{role}</h3>
                  <p className="mt-1 font-semibold text-[#34342f] dark:text-white/80">{org}</p>
                  <p className="mt-4 leading-7 text-[#5d5d53] dark:text-white/65">{detail}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="gallery" className="bg-[#0d1b2a] px-2 py-20 text-white dark:bg-black md:px-4 md:py-28">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-10 lg:grid-cols-[0.75fr_1.25fr]">
              <div className="px-2 lg:sticky lg:top-28 lg:h-fit">
                <p className="mb-3 text-sm uppercase text-[#d5ff77]">Gallery</p>
                <h2 className="text-5xl font-bold leading-tight md:text-7xl">
                  A few frames from the feed.
                </h2>
                <p className="mt-6 max-w-md leading-7 text-white/70">
                  Campus, code, Toronto, robotics, events, and the occasional very serious pair of sunglasses.
                </p>
              </div>

              <div className="grid auto-rows-[250px] grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
                {galleryImages.map((image) => (
                  <figure
                    key={image.src}
                    className={`overflow-hidden rounded-[1.5rem] bg-white/10 ${image.className}`}
                  >
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="h-full w-full object-cover transition duration-500 hover:scale-105"
                      loading="lazy"
                    />
                  </figure>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="playground" className="px-4 py-20 md:px-6 md:py-28">
          <div className="mx-auto max-w-7xl">
            <TicTacToeGame />
          </div>
        </section>

        <section id="contact" className="px-4 py-20 md:px-6 md:py-28">
          <div className="mx-auto max-w-4xl">
            <div className="mb-10 text-center">
              <p className="mb-3 text-sm uppercase text-[#8a3c22]">Contact</p>
              <h2 className="text-5xl font-bold leading-tight md:text-7xl">Let's talk.</h2>
            </div>

            <div className="flex flex-wrap justify-center gap-3">
              <a
                href="mailto:harishkan999@gmail.com"
                className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-5 py-3 text-sm font-semibold transition hover:bg-[#151515] hover:text-white dark:border-white/10 dark:bg-[#171a21] dark:hover:bg-[#d5ff77] dark:hover:text-[#151515]"
              >
                <Mail size={17} />
                Personal
              </a>
              <a
                href="https://github.com/Harish-Kan"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-5 py-3 text-sm font-semibold transition hover:bg-[#151515] hover:text-white dark:border-white/10 dark:bg-[#171a21] dark:hover:bg-[#d5ff77] dark:hover:text-[#151515]"
              >
                <Github size={17} />
                GitHub
              </a>
              <a
                href="https://www.linkedin.com/in/harish-kandavell/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-5 py-3 text-sm font-semibold transition hover:bg-[#151515] hover:text-white dark:border-white/10 dark:bg-[#171a21] dark:hover:bg-[#d5ff77] dark:hover:text-[#151515]"
              >
                <Linkedin size={17} />
                LinkedIn
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-black/10 px-4 py-8 dark:border-white/10 md:px-6">
        <div className="mx-auto flex max-w-7xl flex-col justify-between gap-3 text-sm text-[#5d5d53] dark:text-white/55 md:flex-row">
          <p>&copy; 2026 Harish Karikaran Kandavell. All rights reserved.</p>
          <p>Built in React, kept personal.</p>
        </div>
      </footer>
    </div>
    </div>
  );
}
